import React, {useEffect} from "react";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {Navigate, NavLink, Outlet, useLocation} from "react-router-dom";
import {useAuth} from "hooks/useAuth";
import {adminSectionsFor} from "data/permissions";
import {ViewAsRoleBar, ViewAsRoleControl} from "layout/ViewAsRole";

import "../../App.css";
import "../../css/fonts.css";
import "../../layout/Toolbar/index.css";

import "./index.css";
import "biggerui/styles.css";

const queryClient = new QueryClient();

const AdminContent = () => {
  const {user, role, shortJwt, isLoading} = useAuth();
  const {pathname} = useLocation();

  // Same `window.__renderReady` + `body.image-ready` signals App.js exposes —
  // set once auth has resolved and the page reaches a stable render. Polled
  // by VR. Admin shell never renders bg-transformer / image-upload <img>s,
  // so image-ready can flip true alongside auth-settled.
  const isAuthSettled = !isLoading && (!user || shortJwt);
  useEffect(() => {
    if (isAuthSettled) {
      window.__renderReady = true;
      document.body.classList.add("image-ready");
    }
  }, [isAuthSettled]);

  // Only wait for shortJwt when the user is authenticated — otherwise the
  // issue query is gated (enabled: !!user) and shortJwt stays null forever,
  // trapping the page on "Loading…". Unauthenticated visitors fall through
  // to the role check and see "Access Denied".
  //
  // Known corner case: if the user IS authenticated but /auth/issue 5xxs
  // (degraded mode — the hook returns {token:null}), this guard stays
  // truthy and the admin page stalls on "Loading…". That's intentional:
  // admin API calls require the short token as a Bearer header, so
  // letting the panel render would just surface silent failures.
  if (isLoading || (user && !shortJwt)) {
    return (
      <div className="admin-shell">
        <div className="admin-content">Loading...</div>
      </div>
    );
  }

  const sections = adminSectionsFor(role);
  const sectionPath = pathname.split("/").filter(Boolean)[1];

  // /admin without a section opens the first section the role can use.
  if (sections.length > 0 && !sectionPath) {
    return <Navigate to={`/admin/${sections[0].path}`} replace />;
  }

  if (!sections.some((s) => s.path === sectionPath)) {
    return (
      <div className="admin-shell">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>Your role does not give access to this page.</p>
          <a href="/" className="admin-back-link">
            Go back to app
          </a>
        </div>
        <ViewAsRoleBar />
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <nav className="admin-nav">
        <a href="/" className="admin-back-link">
          Back to app
        </a>
        <div className="admin-nav-links">
          {sections.map((section) => (
            <NavLink
              key={section.path}
              to={`/admin/${section.path}`}
              className={({isActive}) =>
                isActive ? "admin-nav-link admin-nav-link-active" : "admin-nav-link"
              }
            >
              {section.label}
            </NavLink>
          ))}
        </div>
        <ViewAsRoleControl />
      </nav>
      <div className="admin-content">
        <Outlet />
      </div>
    </div>
  );
};

const AdminApp = () => (
  <QueryClientProvider client={queryClient}>
    <AdminContent />
  </QueryClientProvider>
);

export default AdminApp;
